import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { message, Tooltip, Input, Select, Empty, Button, Spin, Modal, Form, InputNumber, Switch } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  HolderOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SearchOutlined,
  ReloadOutlined,
  TagsOutlined,
  FileImageOutlined,
  SyncOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';
import PageBreadcrumbs from '../../../components/common/PageBreadcrumbs';
import ProductDrawer from '../../../components/product/ProductDrawer';
import productService from '../../../services/productService';
import priceListService from '../../../services/priceListService';

const STATUS_COLORS = {
  ACTIVE: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
  DRAFT: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' },
  INACTIVE: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-400' },
};

function ProductTileSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2.5 animate-pulse">
      <div className="flex items-center gap-2.5">
        <div className="w-4 h-4 bg-gray-200 rounded shrink-0" />
        <div className="w-8 h-8 bg-gray-200 rounded-md shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="h-3.5 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded-full w-14" />
          </div>
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
        <div className="flex gap-1.5 shrink-0">
          <div className="h-6 bg-gray-200 rounded w-14" />
          <div className="h-6 bg-gray-200 rounded w-20" />
        </div>
      </div>
    </div>
  );
}

function SortableProductTile({ product, onEdit, onAttributes, canEdit, canViewAttributes, canUpdateBasePrice, onBasePriceClick, onTogglePriceList, togglingPriceListId }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const statusStyle = STATUS_COLORS[product.status] || STATUS_COLORS.DRAFT;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative bg-white border rounded-lg px-3 py-2.5 transition-all hover:shadow-sm ${
        isDragging ? 'shadow-lg border-green-300' : 'border-gray-200 hover:border-green-200'
      }`}
    >
      {/* Status badge: top-right, 50% inside / 50% outside (mobile + desktop) */}
      <span
        className={`inline-flex absolute top-0 right-4 -translate-y-1/2 z-10 items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border shadow-sm ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
      >
        <span className={`w-1 h-1 rounded-full ${statusStyle.dot}`} />
        {product.status}
      </span>

      {/* Mobile: card layout — logo row, then name row */}
      <div className="flex flex-col gap-2 sm:hidden min-w-0">
        <div className="flex min-w-0">
          <div className="w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
            {product.logo_url ? (
              <img
                src={product.logo_url}
                alt={product.short_name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <span
              className={`text-xs font-bold text-gray-400 ${product.logo_url ? 'hidden' : 'flex'}`}
              style={{ display: product.logo_url ? 'none' : 'flex' }}
            >
              {product.short_name?.substring(0, 2).toUpperCase() || '?'}
            </span>
          </div>
        </div>
        <h3 className="font-semibold text-gray-900 text-sm break-words leading-snug min-w-0" title={product.name}>
          {product.name}
        </h3>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-gray-600">
            {product.short_name}
            {product.industry_name && <span className="text-gray-500"> • {product.industry_name}</span>}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          {product.isin_numbers?.length > 0 && (
            <Tooltip title={product.isin_numbers.join(', ')}>
              <span className="text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
                {product.isin_numbers[0]}
                {product.isin_numbers.length > 1 && ` +${product.isin_numbers.length - 1}`}
              </span>
            </Tooltip>
          )}
          {canUpdateBasePrice && onBasePriceClick && (
            <Tooltip title="Click to set or edit base price">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onBasePriceClick(product);
                }}
                className={`text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded border transition-colors ${
                  product.base_price != null && product.base_price !== ''
                    ? 'text-emerald-800 bg-emerald-50 border-emerald-100 hover:bg-emerald-100'
                    : 'text-gray-500 bg-gray-50 border-dashed border-gray-200 hover:border-emerald-300 hover:text-emerald-700'
                }`}
              >
                {product.base_price != null && product.base_price !== '' ? `Base ${product.base_price}` : 'Set base price'}
              </button>
            </Tooltip>
          )}
          {!canUpdateBasePrice && product.base_price != null && product.base_price !== '' && (
            <span className="text-emerald-800 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums">
              Base {product.base_price}
            </span>
          )}
          <Tooltip title={product.is_visible_partner ? 'Visible to Partner' : 'Hidden from Partner'}>
            <span className={product.is_visible_partner ? 'text-green-600' : 'text-gray-500'}>
              {product.is_visible_partner ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              <span className="ml-0.5">Partner</span>
            </span>
          </Tooltip>
          <Tooltip title={product.is_visible_investor ? 'Visible to Investor' : 'Hidden from Investor'}>
            <span className={product.is_visible_investor ? 'text-green-600' : 'text-gray-500'}>
              {product.is_visible_investor ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              <span className="ml-0.5">INV</span>
            </span>
          </Tooltip>
          {canEdit && onTogglePriceList && (
            <Tooltip title={product.include_in_price_list ? 'Included in price list deck' : 'Not in price list deck'}>
              <Switch
                size="small"
                checked={!!product.include_in_price_list}
                loading={togglingPriceListId === product.id}
                onChange={(checked) => onTogglePriceList(product, checked)}
              />
            </Tooltip>
          )}
          {!canEdit && product.include_in_price_list && (
            <Tooltip title="Included in price list deck">
              <CheckOutlined className="text-emerald-600" />
            </Tooltip>
          )}
        </div>

        {(canEdit || canViewAttributes) && (
          <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100">
            {canEdit && (
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0"
              >
                <HolderOutlined className="text-lg" />
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto">
              {canEdit && (
                <Tooltip title="Edit product">
                  <button
                    type="button"
                    onClick={() => onEdit(product)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-green-600"
                  >
                    <EditOutlined />
                    <span>Edit</span>
                  </button>
                </Tooltip>
              )}
              {canViewAttributes && (
                <Tooltip title="Manage attributes">
                  <button
                    type="button"
                    onClick={() => onAttributes?.(product)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                  >
                    <TagsOutlined />
                    <span>Attributes</span>
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Desktop & tablet: single row */}
      <div className="hidden sm:flex items-center gap-2.5">
        {canEdit && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors shrink-0"
          >
            <HolderOutlined />
          </div>
        )}

        <div className="w-8 h-8 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
          {product.logo_url ? (
            <img
              src={product.logo_url}
              alt={product.short_name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <span
            className={`text-[10px] font-bold text-gray-400 ${product.logo_url ? 'hidden' : 'flex'}`}
            style={{ display: product.logo_url ? 'none' : 'flex' }}
          >
            {product.short_name?.substring(0, 2).toUpperCase() || '?'}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm truncate leading-snug flex-1 min-w-0" title={product.name}>
              {product.name}
            </h3>
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 mt-0.5 min-w-0">
            <span className="text-[11px] text-gray-600 truncate min-w-0" title={`${product.short_name}${product.industry_name ? ` • ${product.industry_name}` : ''}`}>
              {product.short_name}
              {product.industry_name && (
                <span className="text-gray-500"> • {product.industry_name}</span>
              )}
            </span>

            <div className="flex items-center justify-center gap-2 text-[10px] shrink-0">
              {product.isin_numbers?.length > 0 && (
                <Tooltip title={product.isin_numbers.join(', ')}>
                  <span className="text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded font-mono truncate max-w-[110px]">
                    {product.isin_numbers[0]}
                    {product.isin_numbers.length > 1 && ` +${product.isin_numbers.length - 1}`}
                  </span>
                </Tooltip>
              )}
              {canUpdateBasePrice && onBasePriceClick && (
                <Tooltip title="Click to set or edit base price">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBasePriceClick(product);
                    }}
                    className={`max-w-[120px] truncate font-mono tabular-nums px-1.5 py-0.5 rounded border text-[10px] transition-colors ${
                      product.base_price != null && product.base_price !== ''
                        ? 'text-emerald-800 bg-emerald-50 border-emerald-100 hover:bg-emerald-100'
                        : 'text-gray-500 bg-gray-50 border-dashed border-gray-200 hover:border-emerald-300 hover:text-emerald-700'
                    }`}
                  >
                    {product.base_price != null && product.base_price !== '' ? `Base ${product.base_price}` : 'Set base price'}
                  </button>
                </Tooltip>
              )}
              {!canUpdateBasePrice && product.base_price != null && product.base_price !== '' && (
                <span className="text-emerald-800 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded font-mono tabular-nums max-w-[100px] truncate">
                  Base {product.base_price}
                </span>
              )}
              <Tooltip title={product.is_visible_partner ? 'Visible to Partner' : 'Hidden from Partner'}>
                <span className={`${product.is_visible_partner ? 'text-green-600' : 'text-gray-500'} flex items-center gap-0.5`}>
                  {product.is_visible_partner ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  <span className="ml-0.5">Partner</span>
                </span>
              </Tooltip>
              <Tooltip title={product.is_visible_investor ? 'Visible to Investor' : 'Hidden from Investor'}>
                <span className={`${product.is_visible_investor ? 'text-green-600' : 'text-gray-500'} flex items-center gap-0.5`}>
                  {product.is_visible_investor ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  <span className="ml-0.5">INV</span>
                </span>
              </Tooltip>
              {canEdit && onTogglePriceList && (
                <Tooltip title={product.include_in_price_list ? 'Included in price list deck' : 'Not in price list deck'}>
                  <Switch
                    size="small"
                    checked={!!product.include_in_price_list}
                    loading={togglingPriceListId === product.id}
                    onChange={(checked) => onTogglePriceList(product, checked)}
                  />
                </Tooltip>
              )}
              {!canEdit && product.include_in_price_list && (
                <Tooltip title="Included in price list deck">
                  <CheckOutlined className="text-emerald-600" />
                </Tooltip>
              )}
              <span className="text-[10px] text-gray-600 font-medium">#{product.preference_index}</span>
            </div>

            <div className="min-w-0" />
          </div>
        </div>

        {(canEdit || canViewAttributes) && (
          <div className="flex items-center gap-1 shrink-0 ml-1">
            {canEdit && (
              <Tooltip title="Edit product">
                <button
                  type="button"
                  onClick={() => onEdit(product)}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-gray-500 hover:bg-gray-100 hover:text-green-600 transition-colors"
                >
                  <EditOutlined />
                  <span>Edit</span>
                </button>
              </Tooltip>
            )}
            {canViewAttributes && (
              <Tooltip title="Manage attributes">
                <button
                  type="button"
                  onClick={() => onAttributes?.(product)}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-gray-500 hover:bg-gray-100 hover:text-blue-600 transition-colors"
                >
                  <TagsOutlined />
                  <span>Attributes</span>
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ListingsPage() {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageProducts = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCTS_CREATE);
  const canEdit = canManageProducts;
  const canViewAttributes = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCTS_VIEW);
  const canUpdateBasePrice = canManageProducts;
  const canGeneratePriceList =
    isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_PRE_IPO_LISTINGS_CREATE);

  const handleAttributesClick = (product) => {
    if (product?.id) navigate(`/pre-ipo/listings/${product.id}/attributes`);
  };

  const PAGE_SIZE = 5; // 5 for testing; use 20 for production

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [industryFilter, setIndustryFilter] = useState(undefined);
  const [industries, setIndustries] = useState([]);
  const [nextPage, setNextPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [basePriceModalOpen, setBasePriceModalOpen] = useState(false);
  const [basePriceProduct, setBasePriceProduct] = useState(null);
  const [basePriceSaving, setBasePriceSaving] = useState(false);
  const [basePriceForm] = Form.useForm();
  const scrollContainerRef = useRef(null);
  const [scrollRoot, setScrollRoot] = useState(null);
  const [generatingDeck, setGeneratingDeck] = useState(false);
  const [togglingPriceListId, setTogglingPriceListId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const buildParams = useCallback(
    (page) => {
      const params = {
        type: 'PRE_IPO',
        page,
        limit: PAGE_SIZE,
        sort_by: 'preference_index',
        sort_order: 'asc',
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (industryFilter) params.industry_id = industryFilter;
      return params;
    },
    [search, statusFilter, industryFilter]
  );

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await productService.getProducts(buildParams(1));
      if (result.success) {
        const data = result.data;
        const list = data?.products ?? data?.data ?? data?.items ?? [];
        setProducts(Array.isArray(list) ? list : []);
        const pag = data?.pagination;
        const totalPages = pag?.total_pages ?? 1;
        const totalCount = pag?.total ?? list.length;
        setTotal(totalCount);
        setHasMore(1 < totalPages);
        setNextPage(2);
      } else {
        setError(result.message || 'Failed to fetch products');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || nextPage === 1) return;
    setLoadingMore(true);
    try {
      const result = await productService.getProducts(buildParams(nextPage));
      if (result.success) {
        const data = result.data;
        const list = data?.products ?? data?.data ?? data?.items ?? [];
        const newList = Array.isArray(list) ? list : [];
        setProducts((prev) => [...prev, ...newList]);
        const pag = data?.pagination;
        const totalPages = pag?.total_pages ?? 1;
        const currentPage = pag?.page ?? nextPage;
        setHasMore(currentPage < totalPages);
        setNextPage((p) => p + 1);
        if (pag?.total != null) setTotal(pag.total);
      }
    } catch (err) {
      console.error('Error loading more products:', err);
      message.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, nextPage, loadingMore, loading]);

  useEffect(() => {
    fetchInitial();
  }, [search, statusFilter, industryFilter, fetchInitial]);

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const result = await productService.getIndustries();
        if (result.success && Array.isArray(result.data)) {
          setIndustries(result.data);
        }
      } catch (err) {
        console.error('Error fetching industries:', err);
      }
    };
    fetchIndustries();
  }, []);

  const setScrollContainerRef = useCallback((el) => {
    scrollContainerRef.current = el;
    setScrollRoot(el);
  }, []);

  const { ref: sentinelRef, inView } = useInView({
    root: scrollRoot,
    rootMargin: '200px',
    threshold: 0,
  });

  useEffect(() => {
    if (inView && hasMore && !loadingMore && !loading) {
      loadMore();
    }
  }, [inView, hasMore, loadingMore, loading, loadMore]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = products.findIndex((p) => p.id === active.id);
    const newIndex = products.findIndex((p) => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newProducts = arrayMove(products, oldIndex, newIndex);
    // Optimistic UI update
    setProducts(newProducts);

    setSavingOrder(true);
    try {
      const uuids = newProducts.map((p) => String(p.id));
      await productService.reorderProducts(uuids);
      message.success('Order saved');
      // Fetch fresh data from backend to get updated preference indices
      fetchInitial();
    } catch (err) {
      console.error('Error updating order:', err);
      const status = err.response?.status;
      const msg = status === 400 ? 'Invalid reorder request' : 'Failed to update order';
      message.error(msg);
      // Revert optimistic update
      fetchInitial();
    } finally {
      setSavingOrder(false);
    }
  };

  const refetchAfterCreateOrUpdate = () => {
    fetchInitial();
  };

  const handleOpenDrawer = (product = null) => {
    if (!canEdit) return;
    setEditingProduct(product);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingProduct(null);
  };

  const openBasePriceModal = useCallback(
    (product) => {
      if (!canUpdateBasePrice) return;
      setBasePriceProduct(product);
      basePriceForm.setFieldsValue({
        base_price:
          product.base_price == null || product.base_price === ''
            ? undefined
            : Number(product.base_price),
      });
      setBasePriceModalOpen(true);
    },
    [basePriceForm, canUpdateBasePrice],
  );

  const closeBasePriceModal = useCallback(() => {
    setBasePriceModalOpen(false);
    setBasePriceProduct(null);
    basePriceForm.resetFields();
  }, [basePriceForm]);

  const handleBasePriceSubmit = async () => {
    if (!basePriceProduct || !canUpdateBasePrice) return;
    try {
      const values = await basePriceForm.validateFields();
      const raw = values.base_price;
      const base_price =
        raw === undefined || raw === null || raw === '' ? null : Number(raw);
      setBasePriceSaving(true);
      const result = await productService.updateProduct(basePriceProduct.id, { base_price });
      if (result.success) {
        message.success('Base price updated');
        closeBasePriceModal();
        fetchInitial();
      } else {
        message.error(result.message || 'Failed to update base price');
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || err.message || 'Failed to update base price');
    } finally {
      setBasePriceSaving(false);
    }
  };

  const getNextPreferenceIndex = () => {
    return total > 0 ? total + 1 : products.length + 1;
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleTogglePriceList = async (product, includeInPriceList) => {
    setTogglingPriceListId(product.id);
    try {
      const result = await productService.updateProduct(product.id, {
        include_in_price_list: includeInPriceList,
      });
      if (result.success) {
        setProducts((prev) =>
          prev.map((p) =>
            p.id === product.id ? { ...p, include_in_price_list: includeInPriceList } : p,
          ),
        );
        message.success(
          includeInPriceList ? 'Product added to price list deck' : 'Product removed from price list deck',
        );
      } else {
        message.error(result.message || 'Failed to update price list flag');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update price list flag');
    } finally {
      setTogglingPriceListId(null);
    }
  };

  const handleViewLatestDeck = async () => {
    try {
      const result = await priceListService.getLatest();
      const imageUrl = result?.data?.image_url;
      if (result.success && imageUrl) {
        window.open(imageUrl, '_blank', 'noopener,noreferrer');
      } else {
        message.warning(result.message || 'No price list deck available yet');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        message.warning('No price list deck has been generated yet');
      } else {
        message.error(err.response?.data?.message || 'Failed to fetch latest deck');
      }
    }
  };

  const handleRegenerateDeck = async () => {
    if (!canGeneratePriceList) return;
    setGeneratingDeck(true);
    try {
      const result = await priceListService.generate();
      if (result.success) {
        message.success(
          `Deck generated (${result.data?.product_count ?? 0} products)`,
        );
        if (result.data?.image_url) {
          window.open(result.data.image_url, '_blank', 'noopener,noreferrer');
        }
      } else {
        message.error(result.message || 'Failed to generate deck');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to generate deck');
    } finally {
      setGeneratingDeck(false);
    }
  };

  const filteredProducts = products;

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden min-h-0 flex flex-col flex-1 h-full">
      <div className="shrink-0">
        <PageBreadcrumbs />
      </div>

      <div
        className={`p-4 sm:p-0 flex flex-col flex-1 min-h-0  overflow-hidden px-4 sm:px-6 pt-4 sm:pt-6 pb-0 sm:pb-0`}
      >
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5 shrink-0 ">
          <div className="flex items-center gap-2 flex-1">
            <Input.Search
              placeholder="Search by name, short name, or ISIN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={handleSearchChange}
              allowClear
              className="max-w-xs"
              prefix={<SearchOutlined className="text-gray-400" />}
            />
            <Select
              placeholder="All Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              className="w-32"
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'DRAFT', label: 'Draft' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
            <Select
              placeholder="All Industries"
              value={industryFilter}
              onChange={setIndustryFilter}
              allowClear
              className="w-40"
              options={industries.map((ind) => ({
                value: ind.id,
                label: ind.name,
              }))}
            />
          </div>

          <div className="flex items-center gap-2">
            {savingOrder && (
              <span className="text-xs text-gray-400 animate-pulse">Saving order…</span>
            )}
            <Tooltip title="View latest price list deck">
              <Button icon={<FileImageOutlined />} onClick={handleViewLatestDeck} />
            </Tooltip>
            {canGeneratePriceList && (
              <Tooltip title="Regenerate price list deck now">
                <Button
                  icon={<SyncOutlined />}
                  onClick={handleRegenerateDeck}
                  loading={generatingDeck}
                />
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchInitial}
                loading={loading}
              />
            </Tooltip>
            {canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenDrawer()}
              >
                Add Product
              </Button>
            )}
          </div>
        </div>

        {/* Hint */}
        {canEdit && !loading && products.length > 1 && (
          <p className="text-xs text-gray-400  flex items-center gap-1 ">
            <HolderOutlined /> Drag tiles to reorder products
          </p>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center mb-4">
            <p className="text-red-600 text-sm">{error}</p>
            <Button size="small" onClick={fetchInitial} className="mt-2">
              Retry
            </Button>
          </div>
        )}

        {/* Loading Skeleton */}
        {loading && (
          <div className="grid gap-4">
            {[...Array(5)].map((_, i) => (
              <ProductTileSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredProducts.length === 0 && (
          <div className="flex items-center justify-center min-h-[350px]">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-gray-500">
                  {search || statusFilter || industryFilter
                    ? 'No products match your filters'
                    : 'No products yet'}
                </span>
              }
            >
            {canEdit && !search && !statusFilter && !industryFilter && (
              <Button type="primary" onClick={() => handleOpenDrawer()}>
                Add First Product
              </Button>
            )}
            </Empty>
          </div>
        )}

        {/* Products: scroll area + footer; flex so footer stays at bottom, no blank below */}
        {!loading && !error && filteredProducts.length > 0 && (
          <div className="flex-1 min-h-0 -mx-4 sm:-mx-6 mt-2 flex flex-col overflow-hidden ">
            <div
              ref={setScrollContainerRef}
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6"
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredProducts.map((p) => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="grid gap-4 pt-3 pb-4">
                    {filteredProducts.map((product) => (
                      <SortableProductTile
                        key={product.id}
                        product={product}
                        onEdit={handleOpenDrawer}
                        onAttributes={handleAttributesClick}
                        canEdit={canEdit}
                        canViewAttributes={canViewAttributes}
                        canUpdateBasePrice={canUpdateBasePrice}
                        onBasePriceClick={canUpdateBasePrice ? openBasePriceModal : undefined}
                        onTogglePriceList={canEdit ? handleTogglePriceList : undefined}
                        togglingPriceListId={togglingPriceListId}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {hasMore && (
                <div ref={sentinelRef} className="h-4 shrink-0" aria-hidden="true" />
              )}
            </div>

            <div className="flex items-center justify-between gap-3 py-3 px-4 sm:px-6 border-t border-gray-200 bg-white shrink-0">
              <p className="text-xs text-gray-600">
                {products.length} product{products.length !== 1 ? 's' : ''}
                {total > 0 && total !== products.length && ` of ${total}`}
              </p>
              {loadingMore && (
                <Spin size="small" tip="Loading more…" />
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        title={basePriceProduct ? `Base price — ${basePriceProduct.name}` : 'Base price'}
        open={basePriceModalOpen}
        onCancel={closeBasePriceModal}
        onOk={handleBasePriceSubmit}
        confirmLoading={basePriceSaving}
        destroyOnClose
        okText="Save"
      >
        <Form form={basePriceForm} layout="vertical" className="mt-2">
          <Form.Item
            name="base_price"
            label="Base price"
            extra="Used with the slab matrix for special pricing tiers. Clear the field to remove."
          >
            <InputNumber
              className="w-full min-w-0"
              min={0}
              step={0.01}
              precision={2}
              placeholder="Optional"
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Product Drawer */}
      <ProductDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        product={editingProduct}
        onSuccess={refetchAfterCreateOrUpdate}
        nextPreferenceIndex={getNextPreferenceIndex()}
      />
    </div>
  );
}
